import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { EditTabModel, EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { AppTextService } from '@fi-shared/app-text';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { OtherSettingsService } from '../other-settings.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import {
    FiCustomerDataService,
    IFiCustomerSupportContact,
} from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { BehaviorSubject } from 'rxjs';
import { UserModel } from '@fi-app/user/user.model';
import { isDefined } from '@fi-shared/utils';
import { SupportContactFrequencyType } from '@fi-shared/types/support-contact-frequency-type';
import { IContactModel } from '@fi-shared/models/common';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';

@Component({
    selector: 'fi-other-settings-support-contact-edit',
    templateUrl: './other-settings-support-contact-edit.component.html',
    styleUrls: ['./other-settings-support-contact-edit.component.scss', '../other-settings-shared.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-other-settings-support-contact-edit' },
    standalone: false,
})
export class OtherSettingsSupportContactEditComponent implements OnInit {
    @Input() supportContact: IFiCustomerSupportContact;

    EditTabs: EditTabsModel;
    selectedSupportEmail: string;
    selectedSupportPhoneNumber: string;
    availableUsers: UserModel[] = [];
    supportManager: UserModel;
    loading$ = new BehaviorSubject(true);

    readonly otherRadioButtonOption: string = 'Other';
    readonly supportContactEmailPropertyName: string = 'EmailAddress';
    readonly supportContactPhoneNumberPropertyName: string = 'PhoneNumber';
    readonly selectUserDisplayProperty: string = 'FullName';
    readonly selectUserValueProperty: string = 'Id';

    FrequencyType = SupportContactFrequencyType;

    constructor(
        private appTextService: AppTextService,
        private saveChangesWarningService: SaveChangesWarningService,
        private otherSettingsService: OtherSettingsService,
        private fiLoadingService: LoadingService,
        private stateService: FiStateService,
        private fiContactsService: FiContactsService,
        private customerDataService: FiCustomerDataService,
    ) {}

    ngOnInit() {
        this.selectedSupportEmail = this.supportContact.EmailAddress;
        this.selectedSupportPhoneNumber = this.supportContact.PhoneNumber;

        this.EditTabs = new EditTabsModel(this.appTextService.get('other_settings.support_contact_settings'));
        this.EditTabs.Tabs.push(
            new EditTabModel(this.appTextService.get('common.general'), this.appTextService.get('common.general')),
        );
        this.saveChangesWarningService.start(() => this.supportContact);

        this.getAvailableUsers();
    }

    getFullContactValue(contact: IContactModel) {
        return this.fiContactsService.getFullContactValue(contact);
    }

    getAvailableUsers() {
        this.loading$.next(true);

        this.otherSettingsService.getSupportManagers().then((users: UserModel[]) => {
            this.availableUsers = users;
            let supportUser = this.availableUsers.find((user: UserModel) => {
                return user.Id === this.supportContact.SupportManagerId;
            });

            /*// try to find by email
              commented out because of https://chainformation.atlassian.net/browse/FI-5182
            if (!supportUser) {
                supportUser = this.availableUsers.find((user: UserModel) => {
                    return user.EmailAddressContacts.findIndex(email => email.ContactValue === this.supportContact.EmailAddress) !== -1;
                });
            }*/

            if (!isDefined(supportUser)) {
                this.selectedSupportEmail = this.otherRadioButtonOption;
                this.selectedSupportPhoneNumber = !this.supportContact.PhoneNumber
                    ? undefined
                    : this.otherRadioButtonOption;
            } else {
                this.supportContact.SupportManagerId = supportUser.Id;
                this.supportManager = supportUser;
                const emailIndex = supportUser.EmailAddressContacts.findIndex(
                    (email) => email.ContactValue === this.supportContact.EmailAddress,
                );
                this.selectedSupportEmail =
                    emailIndex !== -1 ? this.supportContact.EmailAddress : this.otherRadioButtonOption;
                const phoneNumberIndex = supportUser.PhoneNumberContacts.findIndex(
                    (phoneNumber) => this.getFullContactValue(phoneNumber) === this.supportContact.PhoneNumber,
                );
                this.selectedSupportPhoneNumber =
                    phoneNumberIndex !== -1
                        ? this.supportContact.PhoneNumber
                        : this.supportContact.PhoneNumber
                          ? this.otherRadioButtonOption
                          : undefined;
            }

            this.loading$.next(false);
        });
    }

    onSupportContactChange(supportContactTypePropertyName: string, value: string) {
        this.supportContact[supportContactTypePropertyName] = value !== this.otherRadioButtonOption ? value : undefined;
    }

    isCustomFieldShown(contactSupportValue: string): boolean {
        return contactSupportValue === this.otherRadioButtonOption;
    }

    onSupportManagerChange() {
        const user = this.availableUsers.find((u) => u.Id === this.supportContact.SupportManagerId);
        this.supportManager = user;

        if (user) {
            if (!user.PhoneNumberContacts.length) {
                this.selectedSupportPhoneNumber = this.otherRadioButtonOption;
                this.supportContact.PhoneNumber = undefined;
            } else {
                this.selectedSupportPhoneNumber = this.supportContact.PhoneNumber =
                    this.fiContactsService.getFullContactValue(user.PhoneNumberContacts[0]);
            }

            if (!user.EmailAddressContacts.length) {
                this.selectedSupportEmail = this.otherRadioButtonOption;
                this.supportContact.EmailAddress = undefined;
            } else {
                this.selectedSupportEmail = this.supportContact.EmailAddress =
                    user.EmailAddressContacts[0].ContactValue;
            }
        } else {
            this.selectedSupportEmail = this.otherRadioButtonOption;
            this.selectedSupportPhoneNumber = this.otherRadioButtonOption;

            this.supportContact.AllowSupportChat = false;
        }
    }

    save() {
        this.fiLoadingService
            .trackPromise(this.otherSettingsService.saveSupportContact(this.supportContact))
            .then(() => {
                this.saveChangesWarningService.stop();
                this.stateService.go(FiStateNames.OtherSettings.List);
            });
    }

    onEmailAddressChange($event) {
        if (!$event) {
            this.supportContact.FrequencyType = SupportContactFrequencyType.None;
        }
    }

    isFrequencyBlockEnabled() {
        return this.customerDataService.customerData.MobileAppUsage;
    }
}
